import { useCallback, useEffect, useState } from 'react';
import { api, type Integration, type Settings, type WhatsappMode } from '../api.js';
import { WhatsappChannelPanel } from './WhatsappChannelPanel.js';
import { WhatsappSession } from './WhatsappSession.js';
import { PageHead } from './PageHead.js';

const STATUS_BADGE: Record<Integration['status'], { cls: string; label: string }> = {
  CONNECTED: { cls: 'ok', label: 'conectado' },
  DISCONNECTED: { cls: '', label: 'não configurado' },
  ERROR: { cls: 'err', label: 'com erro' },
  EXPIRED: { cls: 'warn', label: 'expirado' },
};

export function Integrations({ mockMode }: { mockMode: boolean }) {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  // Quais adaptadores estão chamando a API real. Vem do mesmo endpoint, e é a
  // informação que muda o que esperar do próximo agendamento.
  const [aoVivo, setAoVivo] = useState<string[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'info' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Formulários
  const [shopee, setShopee] = useState({ appId: '', secret: '' });
  const [instagram, setInstagram] = useState({ igUserId: '', accessToken: '' });
  const [tiktok, setTiktok] = useState({ accessToken: '', refreshToken: '', openId: '' });
  const [whatsapp, setWhatsapp] = useState({
    mode: 'notify_manual' as WhatsappMode,
    phoneNumberId: '',
    accessToken: '',
    recipients: '',
    groupId: '',
    groupJid: '',
    groupName: '',
    canalNome: '',
  });

  const load = useCallback(async () => {
    const [i, s] = await Promise.all([api.integrations(), api.settings()]);
    setIntegrations(i.integrations);
    setAoVivo(i.liveAdapters ?? []);
    setSettings(s);

    // Reidrata o formulário do WhatsApp. Sem isso, quem salvou "aparelho
    // pareado" reabriria a página vendo "avisar para eu postar" e concluiria
    // que a automação está desligada — quando na verdade está publicando.
    const wa = i.integrations.find((x) => x.provider === 'WHATSAPP');
    const f = wa?.fields ?? {};
    if (f.mode) {
      setWhatsapp((w) => ({
        ...w,
        mode: f.mode as WhatsappMode,
        ...(f.phoneNumberId ? { phoneNumberId: f.phoneNumberId } : {}),
        ...(f.groupJid ? { groupJid: f.groupJid } : {}),
        ...(f.grupo ? { groupName: f.grupo } : {}),
        // Sempre reescrito: desligar o canal precisa sumir da tela também.
        canalNome: f.canal ?? '',
      }));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Devolve true quando gravou. Quem precisa reagir ao sucesso usa o retorno. */
  async function save(
    key: string,
    fn: () => Promise<unknown>,
    successText: string,
  ): Promise<boolean> {
    setBusy(key);
    setMessage(null);
    try {
      await fn();
      setMessage({ kind: 'ok', text: successText });
      await load();
      return true;
    } catch (cause) {
      setMessage({
        kind: 'err',
        text: cause instanceof Error ? cause.message : 'Falha ao salvar',
      });
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function patchSettings(patch: Partial<Settings>) {
    setSettings(await api.saveSettings(patch));
  }

  const conectadas = integrations.filter((i) => i.status === 'CONNECTED').length;

  const byProvider = (p: Integration['provider']) =>
    integrations.find((i) => i.provider === p);

  return (
    <>
      <PageHead
        eyebrow="Integrações"
        title="Quem está no ar"
        description="Suas credenciais ficam cifradas (AES-256-GCM) no banco e nunca são devolvidas em texto claro — só os últimos 4 caracteres, para conferência."
        readout={[
          {
            label: conectadas === 1 ? 'conta conectada' : 'contas conectadas',
            value: conectadas,
            tom: conectadas > 0 ? ('gain' as const) : undefined,
          },
          // "Publicando de verdade" é a informação que muda o que a pessoa
          // pode esperar do próximo agendamento — merece estar no topo, não
          // escondida no aviso de modo mock.
          {
            label: aoVivo.length === 1 ? 'publicando de verdade' : 'publicando de verdade',
            value: aoVivo.length === 0 ? 'nenhuma' : aoVivo.join(', '),
            tom: aoVivo.length === 0 ? ('quiet' as const) : undefined,
          },
        ]}
      />

      {mockMode && (
        <div className="notice info">
          <strong>Modo mock ativo.</strong> Nenhuma chamada externa real é feita: a Shopee devolve
          produtos sintéticos e as publicações são simuladas. Para usar as APIs de verdade,
          coloque <span className="mono">MOCK_MODE=false</span> no{' '}
          <span className="mono">apps/api/.env</span>.
        </div>
      )}

      {message && <div className={`notice ${message.kind}`}>{message.text}</div>}

      <div style={{ display: 'grid', gap: 16 }}>
        {/* --------------------------------------------------------- Shopee */}
        <IntegrationCard
          title="Shopee Afiliados"
          hint="App ID e Secret do painel de afiliado (affiliate.shopee.com.br → Open API). Validamos antes de salvar."
          integration={byProvider('SHOPEE')}
        >
          <div className="row">
            <div>
              <label htmlFor="shopeeAppId">App ID</label>
              <input
                id="shopeeAppId"
                      autoComplete="off"
                value={shopee.appId}
                onChange={(e) => setShopee({ ...shopee, appId: e.target.value })}
                placeholder="1234567890"
              />
            </div>
            <div>
              <label htmlFor="shopeeSecret">Secret</label>
              <input
                id="shopeeSecret"
                      autoComplete="new-password"
                type="password"
                value={shopee.secret}
                onChange={(e) => setShopee({ ...shopee, secret: e.target.value })}
              />
            </div>
            <button
              type="button"
              className="primary"
              style={{ flex: 'none', alignSelf: 'flex-end' }}
              disabled={busy === 'shopee' || !shopee.appId || !shopee.secret}
              onClick={() =>
                void save('shopee', () => api.saveShopee(shopee), 'Shopee conectada.')
              }
            >
              {busy === 'shopee' ? <span className="spinner" /> : 'Salvar'}
            </button>
          </div>
        </IntegrationCard>

        {/* ------------------------------------------------------ Instagram */}
        <IntegrationCard
          title="Instagram"
          hint="Conta Business/Creator vinculada a uma Página. Use um token de longa duração (Usuário do Sistema não expira)."
          integration={byProvider('INSTAGRAM')}
        >
          <div className="row">
            <div>
              <label htmlFor="igUserId">Instagram Business Account ID</label>
              <input
                id="igUserId"
                      autoComplete="off"
                value={instagram.igUserId}
                onChange={(e) => setInstagram({ ...instagram, igUserId: e.target.value })}
                placeholder="17841400000000000"
              />
            </div>
            <div>
              <label htmlFor="igToken">Access token (longa duração)</label>
              <input
                id="igToken"
                      autoComplete="new-password"
                type="password"
                value={instagram.accessToken}
                onChange={(e) => setInstagram({ ...instagram, accessToken: e.target.value })}
                placeholder="EAAG…"
              />
            </div>
            <button
              type="button"
              className="primary"
              style={{ flex: 'none', alignSelf: 'flex-end' }}
              disabled={busy === 'ig' || !instagram.igUserId || !instagram.accessToken}
              onClick={() =>
                void save('ig', () => api.saveInstagram(instagram), 'Instagram conectado.')
              }
            >
              {busy === 'ig' ? <span className="spinner" /> : 'Salvar'}
            </button>
          </div>
        </IntegrationCard>

        {/* --------------------------------------------------------- TikTok */}
        <IntegrationCard
          title="TikTok"
          hint="O access token vive 24h e é renovado sozinho pelo refresh token (365 dias). Antes do audit de video.publish, todo post sai como privado."
          integration={byProvider('TIKTOK')}
        >
          <div className="row">
            <div>
              <label htmlFor="ttAccess">Access token</label>
              <input
                id="ttAccess"
                      autoComplete="new-password"
                type="password"
                value={tiktok.accessToken}
                onChange={(e) => setTiktok({ ...tiktok, accessToken: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="ttRefresh">Refresh token</label>
              <input
                id="ttRefresh"
                      autoComplete="new-password"
                type="password"
                value={tiktok.refreshToken}
                onChange={(e) => setTiktok({ ...tiktok, refreshToken: e.target.value })}
              />
            </div>
            <div style={{ maxWidth: 150 }}>
              <label htmlFor="ttOpenId">Open ID (opcional)</label>
              <input
                id="ttOpenId"
                      autoComplete="off"
                value={tiktok.openId}
                onChange={(e) => setTiktok({ ...tiktok, openId: e.target.value })}
              />
            </div>
            <button
              type="button"
              className="primary"
              style={{ flex: 'none', alignSelf: 'flex-end' }}
              disabled={busy === 'tt' || !tiktok.accessToken || !tiktok.refreshToken}
              onClick={() =>
                void save(
                  'tt',
                  () =>
                    api.saveTiktok({
                      accessToken: tiktok.accessToken,
                      refreshToken: tiktok.refreshToken,
                      ...(tiktok.openId ? { openId: tiktok.openId } : {}),
                    }),
                  'TikTok conectado.',
                )
              }
            >
              {busy === 'tt' ? <span className="spinner" /> : 'Salvar'}
            </button>
          </div>
        </IntegrationCard>

        {/* ------------------------------------------------------- WhatsApp */}
        <IntegrationCard
          title="WhatsApp"
          hint="Recomendado começar em “avisar para eu postar”: zero risco de banimento e não depende de aprovação. Disparo automático fora da janela de 24h exige template de MARKETING aprovado e opt-in."
          integration={byProvider('WHATSAPP')}
        >
          <div className="form-grid">
            <div style={{ maxWidth: 340 }}>
              <label htmlFor="waMode">Modo de envio</label>
              <select
                id="waMode"
                value={whatsapp.mode}
                onChange={(e) =>
                  setWhatsapp({ ...whatsapp, mode: e.target.value as typeof whatsapp.mode })
                }
              >
                <option value="notify_manual">
                  Avisar para eu postar (recomendado, sem risco)
                </option>
                <option value="cloud_api">Cloud API — lista de números com opt-in</option>
                <option value="groups_api">Groups API — grupo (precisa elegibilidade)</option>
                <option value="web_automation">
                  Aparelho pareado — grupo, automático (fora dos termos do WhatsApp)
                </option>
              </select>
            </div>

            {whatsapp.mode === 'web_automation' && (
              <WhatsappSession
                selectedJid={whatsapp.groupJid || null}
                selectedName={whatsapp.groupName || null}
                onSelectGroup={async (g) => {
                  // Grava na hora: sem isso o worker publicaria no grupo antigo
                  // até o usuário lembrar de clicar em Salvar.
                  const gravou = await save(
                    'wa',
                    () =>
                      api.saveWhatsapp({
                        mode: 'web_automation',
                        groupJid: g.jid,
                        groupName: g.name,
                      }),
                    `Publicando automaticamente em “${g.name}”.`,
                  );
                  // Só reflete na tela o que de fato foi gravado — senão a UI
                  // mostraria um grupo escolhido que o worker não conhece.
                  if (gravou) {
                    setWhatsapp((w) => ({ ...w, groupJid: g.jid, groupName: g.name }));
                  }
                  return gravou;
                }}
              />
            )}

            {whatsapp.mode === 'web_automation' && whatsapp.groupJid && (
              <WhatsappChannelPanel canalNome={whatsapp.canalNome || null} onMudou={load} />
            )}

            {whatsapp.mode !== 'notify_manual' && whatsapp.mode !== 'web_automation' && (
              <>
                <div className="row">
                  <div>
                    <label htmlFor="waPhone">Phone Number ID</label>
                    <input
                      id="waPhone"
                      autoComplete="off"
                      value={whatsapp.phoneNumberId}
                      onChange={(e) =>
                        setWhatsapp({ ...whatsapp, phoneNumberId: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label htmlFor="waToken">Access token</label>
                    <input
                      id="waToken"
                      autoComplete="new-password"
                      type="password"
                      value={whatsapp.accessToken}
                      onChange={(e) => setWhatsapp({ ...whatsapp, accessToken: e.target.value })}
                    />
                  </div>
                </div>
                {whatsapp.mode === 'groups_api' ? (
                  <div>
                    <label htmlFor="waGroup">ID do grupo</label>
                    <input
                      id="waGroup"
                      autoComplete="off"
                      value={whatsapp.groupId}
                      onChange={(e) => setWhatsapp({ ...whatsapp, groupId: e.target.value })}
                    />
                  </div>
                ) : (
                  <div>
                    <label htmlFor="waRecipients">
                      Números de destino (E.164, separados por vírgula)
                    </label>
                    <input
                      id="waRecipients"
                      autoComplete="off"
                      value={whatsapp.recipients}
                      onChange={(e) => setWhatsapp({ ...whatsapp, recipients: e.target.value })}
                      placeholder="5511999999999, 5511888888888"
                    />
                  </div>
                )}
              </>
            )}

            <button
              type="button"
              className="primary"
              style={{ maxWidth: 160 }}
              disabled={busy === 'wa' || (whatsapp.mode === 'web_automation' && !whatsapp.groupJid)}
              onClick={() =>
                void save(
                  'wa',
                  () =>
                    api.saveWhatsapp({
                      mode: whatsapp.mode,
                      ...(whatsapp.phoneNumberId
                        ? { phoneNumberId: whatsapp.phoneNumberId }
                        : {}),
                      ...(whatsapp.accessToken ? { accessToken: whatsapp.accessToken } : {}),
                      ...(whatsapp.groupId ? { groupId: whatsapp.groupId } : {}),
                      ...(whatsapp.groupJid
                        ? { groupJid: whatsapp.groupJid, groupName: whatsapp.groupName }
                        : {}),
                      ...(whatsapp.recipients
                        ? {
                            recipients: whatsapp.recipients
                              .split(',')
                              .map((r) => r.trim())
                              .filter(Boolean),
                          }
                        : {}),
                    }),
                  'WhatsApp configurado.',
                )
              }
            >
              {busy === 'wa' ? <span className="spinner" /> : 'Salvar'}
            </button>
          </div>
        </IntegrationCard>

        {/* ---------------------------------------------------- preferências */}
        {settings && (
          <section className="card" style={{ padding: 18 }}>
            <p className="section-title">Preferências de automação e de copy</p>
            <div className="form-grid">
              <label className="check">
                <input
                  type="checkbox"
                  checked={settings.discoveryEnabled}
                  onChange={(e) => void patchSettings({ discoveryEnabled: e.target.checked })}
                />
                Buscar produtos automaticamente
              </label>

              <div className="row">
                <div>
                  <label htmlFor="interval">Intervalo da busca (horas)</label>
                  <input
                    id="interval"
                    type="number"
                    min={1}
                    max={168}
                    value={settings.discoveryIntervalHours}
                    onChange={(e) =>
                      void patchSettings({ discoveryIntervalHours: Number(e.target.value) })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="minComm">Comissão mínima (%)</label>
                  <input
                    id="minComm"
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={(settings.discoveryMinCommission * 100).toFixed(1)}
                    onChange={(e) =>
                      void patchSettings({
                        discoveryMinCommission: Number(e.target.value) / 100,
                      })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="limit">Produtos por busca</label>
                  <input
                    id="limit"
                    type="number"
                    min={1}
                    max={50}
                    value={settings.discoveryLimit}
                    onChange={(e) => void patchSettings({ discoveryLimit: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label htmlFor="igCap" title="A Graph API rejeita a 26ª publicação em 24h">
                    Teto diário do Instagram
                  </label>
                  <input
                    id="igCap"
                    type="number"
                    min={1}
                    max={50}
                    value={settings.igDailyCap}
                    onChange={(e) => void patchSettings({ igDailyCap: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div style={{ maxWidth: 340 }}>
                <label htmlFor="hashtags">Hashtags fixas do Instagram</label>
                <input
                  id="hashtags"
                  defaultValue={(settings.baseHashtags ?? []).join(', ')}
                  onBlur={(e) =>
                    void patchSettings({
                      baseHashtags: e.target.value
                        .split(',')
                        .map((h) => h.trim().replace(/^#/, ''))
                        .filter(Boolean),
                    })
                  }
                  placeholder="publi, shopee, achadinhos"
                />
                <span className="muted" style={{ fontSize: 11.5 }}>
                  Todas vão no bloco final da legenda. A <strong>primeira</strong> abre o bloco —
                  deixe nela a identificação de publicidade (<span className="mono">publi</span>),
                  que o CONAR recomenda manter visível e fora do meio das outras.
                </span>
              </div>

              <div>
                <label htmlFor="ctaComment">Chamada para comentar</label>
                <input
                  id="ctaComment"
                  defaultValue={settings.ctaComment}
                  onBlur={(e) => void patchSettings({ ctaComment: e.target.value })}
                  placeholder="Para receber o link do produto comente EU QUERO."
                />
              </div>

              <div>
                <label htmlFor="ctaStories">Aviso sobre Stories e destaques</label>
                <input
                  id="ctaStories"
                  defaultValue={settings.ctaStories}
                  onBlur={(e) => void patchSettings({ ctaStories: e.target.value })}
                  placeholder="Confira o link também nos Stories nas primeiras 24h…"
                />
                <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
                  <span className="mono">{'{destaque}'}</span> vira o nome do destaque
                  abaixo. Sem o marcador, o nome não entra em lugar nenhum.
                </p>
              </div>

              <div style={{ maxWidth: 260 }}>
                <label htmlFor="igHighlight">Nome do destaque do Instagram</label>
                <input
                  id="igHighlight"
                  defaultValue={settings.igHighlightName ?? ''}
                  onBlur={(e) =>
                    void patchSettings({ igHighlightName: e.target.value.trim() || null })
                  }
                  placeholder="BRINQUEDOS"
                />
                <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
                  Muda por categoria do que você posta. Deixe vazio se não usa destaques.
                </p>
              </div>

              <div style={{ maxWidth: 340 }}>
                <label htmlFor="keyword">Palavra-chave da busca (opcional)</label>
                <input
                  id="keyword"
                  value={settings.discoveryKeyword ?? ''}
                  onChange={(e) =>
                    void patchSettings({ discoveryKeyword: e.target.value || null })
                  }
                  placeholder="ex: cozinha, fitness"
                />
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function IntegrationCard({
  title,
  hint,
  integration,
  children,
}: {
  title: string;
  hint: string;
  integration: Integration | undefined;
  children: React.ReactNode;
}) {
  const status = integration?.status ?? 'DISCONNECTED';
  const badge = STATUS_BADGE[status];
  return (
    <section className="card" style={{ padding: 18 }}>
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <strong style={{ fontSize: 14 }}>{title}</strong>
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
        {integration && Object.entries(integration.fields).length > 0 && (
          <span className="muted mono">
            {Object.entries(integration.fields)
              .map(([k, v]) => `${k}=${v}`)
              .join('  ')}
          </span>
        )}
      </div>
      <p className="muted" style={{ marginTop: 0, marginBottom: 14, maxWidth: '72ch' }}>
        {hint}
      </p>
      {integration?.lastError && (
        <div className="notice err" style={{ fontSize: 12 }}>
          Último erro: {integration.lastError}
        </div>
      )}
      {children}
    </section>
  );
}
